import { useEffect } from 'react';
import { IPage } from 'store/donatePage/types';
import { IEvent } from 'store/eventPage/types';
import { ProcessingRedirectOptions } from 'store/donatePage/constants';
import { datadogLogs } from '@datadog/browser-logs';
import { watchShimErrors } from 'helpers/pixel';

const PARTNER_TRACKING_ATTR = 'data-partner-tracking';

// Event names as attributed by respective platforms. GA4 only records revenue from
// 'purchase', Google Ads only counts 'conversion', and Meta only attributes
// standard names sent via trackSingle. (For manual testing with Oath-owned IDs,
// swap in distinctive test names — but Meta then requires the trackSingleCustom
// command, not trackSingle.)
const EVENT_NAMES = {
  META_PAGE_VIEW: 'PageView',
  META_PURCHASE: 'Purchase',
  GOOGLE_ADS_CONVERSION: 'conversion',
  GA4_PURCHASE: 'purchase',
} as const;

// IDs already initialized this SPA lifetime. Module-scoped (not a ref) so init
// survives StrictMode's double-invoke and SPA re-navigations without re-firing
// init/PageView/config. A real page reload resets it, so reloads still fire
// PageView — sessionStorage would suppress that for the whole tab session.
const initializedPartnerIds = new Set<string>();

// Partner IDs are stored data, so they're only ever passed to fbq/gtag as
// function arguments (never interpolated into script source). These per-provider
// allowlists are defense-in-depth on top of that.
const VALID_TRACKING_ID: Record<string, RegExp> = {
  meta: /^\d{1,20}$/, // Meta pixel IDs are purely numeric
  google_ads: /^AW-[A-Z0-9]+$/i, // Google Ads conversion account, e.g. AW-123456789
  google_analytics: /^(G|GT)-[A-Z0-9]+$/i, // GA4 property / Google tag, e.g. G-XXXXXXX
};

// Log context carries page_query_str (not the raw partner ID) so Datadog
// monitors can identify which page/partner tripped a warning without logging
// external account IDs.
function isValidTrackingId(
  id: string,
  provider: keyof typeof VALID_TRACKING_ID,
  pageQueryStr: string | undefined
): boolean {
  const valid = VALID_TRACKING_ID[provider].test(id);
  if (!valid) {
    datadogLogs.logger.warn(`Error in Partner Tracking: ID validation failed`, {
      provider,
      page_query_str: pageQueryStr,
    });
  }
  return valid;
}

// The fbq stub in index.html is defined synchronously on every page (even if
// fbevents.js itself is blocked), so the partner pixel only needs init + a
// scoped PageView. trackSingle targets just the partner's ID, so it won't
// cross-fire to Oath's own pixels.
function initMetaPixel(metaId: string, pageQueryStr: string | undefined) {
  if (initializedPartnerIds.has(metaId)) return;
  if (typeof window.fbq !== 'function') {
    datadogLogs.logger.warn(`Error in Partner Tracking: fbq is not available`, {
      provider: 'meta',
      page_query_str: pageQueryStr,
    });
    return;
  }
  initializedPartnerIds.add(metaId);
  // Before init, so a foreign broadcast can't land in the gap.
  window.__oathPixelShim?.registerPartner(metaId);
  // Keeps Meta's auto-detected events and page metadata off the partner's pixel.
  window.fbq('set', 'autoConfig', false, metaId);
  window.fbq('init', metaId);
  window.fbq('trackSingle', metaId, EVENT_NAMES.META_PAGE_VIEW);
}

// Google gtag is inherently scoped — gtag('config', id) and send_to
// target a specific ID, so no cross-talk with Oath's own gtag setup.
// The gtag function AND its library are already loaded by index.html, so we
// only config the partner's ID(s) on the existing library (gtag fetches each
// extra container itself). A partner may have both a Google Ads ID and a GA4
// ID — each gets its own config call.
function injectGoogleTag(
  googleIds: string[],
  pageQueryStr: string | undefined
): HTMLScriptElement | null {
  const newIds = googleIds.filter(id => !initializedPartnerIds.has(id));
  if (!newIds.length) return null;
  const gtag = window.gtag;
  if (typeof gtag !== 'function') {
    datadogLogs.logger.warn(`Error in Partner Tracking: gtag is not available`, {
      provider: 'google',
      page_query_str: pageQueryStr,
    });
    return null;
  }

  newIds.forEach(id => initializedPartnerIds.add(id));

  // DOM-level idempotency guard: only inject a gtag container when NO
  // `googletagmanager.com/gtag/js` script exists anywhere on the page. index.html
  // always ships Oath's own gtag.js (AW-…), so in practice this branch is dead —
  // it's defensive in case that tag is ever dropped. A second gtag.js *library*
  // load corrupts the shared gtag state (`is_legacy_loaded`), so we never add one;
  // partner IDs attach to the existing library via the `gtag('config', id)` calls
  // below, which fetch each extra container themselves.
  let gtagScript: HTMLScriptElement | null = null;
  if (!document.querySelector('script[src*="googletagmanager.com/gtag/js"]')) {
    gtagScript = document.createElement('script');
    gtagScript.async = true;
    gtagScript.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(newIds[0])}`;
    gtagScript.setAttribute(PARTNER_TRACKING_ATTR, 'google-src');
    document.head.appendChild(gtagScript);
  }

  newIds.forEach(id => gtag('config', id));

  return gtagScript;
}

/** Fire partner tracking init + PageView on mount. Use on donate/event pages. */
export const usePartnerTrackingInit = (page: IPage | IEvent | undefined) => {
  useEffect(() => {
    // Above the early returns: a degraded shim cross-fires on any page with a foreign
    // broadcast, not just the ones that end up configuring a partner pixel.
    watchShimErrors();

    if (page?.processing_redirect !== ProcessingRedirectOptions.STRIPE) return;
    const partnerTracking = page?.partner_tracking;
    if (!partnerTracking) return;

    const injectedScripts: HTMLElement[] = [];

    if (
      partnerTracking.meta_id &&
      isValidTrackingId(partnerTracking.meta_id, 'meta', page?.query_str)
    ) {
      initMetaPixel(partnerTracking.meta_id, page?.query_str);
    }

    const googleIds: string[] = [];
    if (
      partnerTracking.google_ads_id &&
      isValidTrackingId(partnerTracking.google_ads_id, 'google_ads', page?.query_str)
    ) {
      googleIds.push(partnerTracking.google_ads_id);
    }
    if (
      partnerTracking.google_analytics_id &&
      isValidTrackingId(partnerTracking.google_analytics_id, 'google_analytics', page?.query_str)
    ) {
      googleIds.push(partnerTracking.google_analytics_id);
    }
    if (googleIds.length) {
      const gtagScript = injectGoogleTag(googleIds, page?.query_str);
      if (gtagScript) injectedScripts.push(gtagScript);
    }

    return () => {
      injectedScripts.forEach(el => el.remove());
    };
    // Depend on the primitive IDs, not the partner_tracking object — the page
    // reducer produces a fresh object reference on every fetch, which would
    // re-run this effect (another PageView + gtag config) on identical re-fetches.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    page?.partner_tracking?.meta_id,
    page?.partner_tracking?.google_ads_id,
    page?.partner_tracking?.google_analytics_id,
    page?.processing_redirect,
  ]);
};

/** Fire partner tracking conversion event on successful donation. Use on Stripe confirmation page. */
export const usePartnerTrackingConversion = (
  page: IPage | IEvent | undefined,
  isDonationSuccessful: boolean,
  paymentIntentId?: string,
  donationAmountCents?: number
) => {
  useEffect(() => {
    if (!isDonationSuccessful || !paymentIntentId) return;
    if (page?.processing_redirect !== ProcessingRedirectOptions.STRIPE) return;

    const partnerTracking = page?.partner_tracking;
    if (!partnerTracking) return;

    const valueDollars = donationAmountCents ? donationAmountCents / 100 : 0;

    try {
      // sessionStorage keyed on the payment intent fires a given conversion
      // at most once per intent.
      const firedKey = `partner_conversion_fired:${paymentIntentId}`;
      if (sessionStorage.getItem(firedKey)) return;
      sessionStorage.setItem(firedKey, '1');

      if (partnerTracking.meta_id && typeof window.fbq === 'function') {
        // trackSingle scopes the Purchase event to only the partner's pixel ID, so it won't cross-fire to Oath's own pixels
        // this pattern is also used in usePartnerTrackingInit via initMetaPixel
        window.fbq('trackSingle', partnerTracking.meta_id, EVENT_NAMES.META_PURCHASE, {
          value: valueDollars,
          currency: 'USD',
        });
      }
      // Google Ads records conversions via a 'conversion' event, but GA4 only
      // registers purchases as a 'purchase' event with a transaction payload —
      // a bare 'conversion' event is silently ignored by GA4. transaction_id
      // also lets both products dedupe repeat fires server-side.
      if (partnerTracking.google_ads_id && typeof window.gtag === 'function') {
        window.gtag('event', EVENT_NAMES.GOOGLE_ADS_CONVERSION, {
          send_to: partnerTracking.google_ads_id,
          value: valueDollars,
          currency: 'USD',
          transaction_id: paymentIntentId,
        });
      }
      if (partnerTracking.google_analytics_id && typeof window.gtag === 'function') {
        window.gtag('event', EVENT_NAMES.GA4_PURCHASE, {
          send_to: partnerTracking.google_analytics_id,
          value: valueDollars,
          currency: 'USD',
          transaction_id: paymentIntentId,
        });
      }
    } catch (error) {
      datadogLogs.logger.warn(
        `Error in Partner Tracking: conversion`,
        { payment_intent_id: paymentIntentId, page_query_str: page?.query_str },
        error instanceof Error ? error : new Error(String(error))
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDonationSuccessful, paymentIntentId]);
};
